import React, { useState, useEffect } from 'react';
import { Plus, Settings, Search } from 'lucide-react';
import Button from '../../components/ui/Button';
import ServiceModal from './components/ServiceModal';
import CategoryModal from './components/CategoryModal';
import { serviceConfigService } from '../../services/serviceConfigService';
import { useAuth } from '../../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import ComponentIdBadge from '../../components/ui/ComponentIdBadge';
import Sidebar from '../../components/ui/Sidebar';
import Header from '../../components/ui/Header';
import SampleDataToggle from '../../components/ui/SampleDataToggle';
import { sampleDataService } from '../../services/sampleDataService';
import { useRealtimeEntity } from '../../hooks/useRealtimeEntity';



export default function ServiceConfiguration() {
  const { user, isSuperAdmin, profile, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('services');
  const [services, setServices] = useState([]);
  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showServiceModal, setShowServiceModal] = useState(false);
  const [showCategoryModal, setShowCategoryModal] = useState(false);
  const [selectedService, setSelectedService] = useState(null);
  const [selectedCategoryData, setSelectedCategoryData] = useState(null);
  const [showSampleData, setShowSampleData] = useState(false);
  const [sampleServices, setSampleServices] = useState([]);

  // Get tenant_id - Super Admins can view all (pass null), others use their tenant
  const tenantId = isSuperAdmin() ? null : (profile?.tenant_id || user?.user_metadata?.tenant_id || user?.raw_user_meta_data?.tenant_id);

  // Add authentication check - wait for auth to finish loading
  useEffect(() => {
    if (!authLoading && !user) {
      navigate('/login');
    }
  }, [user, authLoading, navigate]);

  // Load data on mount - Super Admins don't need tenantId
  useEffect(() => {
    if (user) {
      loadData();
    }
  }, [user]);

  // Real-time services synchronization
  const transformService = (dbService) => ({
    id: dbService?.id,
    tenantId: dbService?.tenant_id,
    name: dbService?.name,
    description: dbService?.description,
    categoryId: dbService?.category_id,
    duration: dbService?.duration,
    price: dbService?.price,
    isActive: dbService?.is_active,
    requiresApproval: dbService?.requires_approval,
    metadata: dbService?.metadata,
    createdAt: dbService?.created_at,
    updatedAt: dbService?.updated_at
  });

  const { data: realtimeServices, setData: setRealtimeServices, isConnected } = useRealtimeEntity(
    'services',
    services,
    transformService
  );

  // Sync real-time data with local state
  useEffect(() => {
    if (isConnected) {
      setServices(realtimeServices);
    }
  }, [realtimeServices, isConnected]);

  const loadData = async () => {
    try {
      setLoading(true);
      setError('');

      const [servicesData, categoriesData] = await Promise.all([
        serviceConfigService?.getAllServices(tenantId),
        serviceConfigService?.getAllCategories(tenantId)
      ]);

      setServices(servicesData || []);
      setCategories(categoriesData || []);
    } catch (err) {
      setError(err?.message || 'Failed to load service configuration');
      console.error('Error loading service configuration:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleSampleDataToggle = async (isActive) => {
    try {
      setShowSampleData(isActive);
      if (isActive) {
        const sampleData = await sampleDataService?.getSampleDataByEntity('services', tenantId);
        setSampleServices(sampleData);
      } else {
        setSampleServices([]);
      }
    } catch (err) {
      console.error('Error loading sample data:', err);
      setError('Failed to load sample data');
    }
  };

  const filteredServices = services?.filter(service => {
    const matchesCategory = selectedCategory === 'all' || service?.category_id === selectedCategory;
    const matchesSearch = searchQuery === '' ||
      service?.name?.toLowerCase()?.includes(searchQuery?.toLowerCase()) ||
      service?.description?.toLowerCase()?.includes(searchQuery?.toLowerCase());
    
    return matchesCategory && matchesSearch;
  });

  // Combine real and sample data if sample data is active
  const displayServices = showSampleData ? [...filteredServices, ...sampleServices] : filteredServices;

  const handleAddService = () => {
    setSelectedService(null);
    setShowServiceModal(true);
  };

  const handleEditService = (service) => {
    setSelectedService(service);
    setShowServiceModal(true);
  };

  const handleDeleteService = async (serviceId) => {
    if (!window.confirm('Are you sure you want to delete this service?')) {
      return;
    }

    try {
      await serviceConfigService?.deleteService(serviceId);
      loadData();
    } catch (err) {
      console.error('Error deleting service:', err);
      setError(err?.message);
    }
  };

  const handleToggleService = async (serviceId, currentStatus) => {
    try {
      await serviceConfigService?.updateService(serviceId, {
        is_active: !currentStatus
      });
      loadData();
    } catch (err) {
      console.error('Error toggling service:', err);
      setError(err?.message);
    }
  };

  const handleSaveService = async (serviceData) => {
    try {
      if (selectedService) {
        await serviceConfigService?.updateService(selectedService?.id, serviceData);
      } else {
        await serviceConfigService?.createService({ ...serviceData, tenant_id: tenantId });
      }
      setShowServiceModal(false);
      setSelectedService(null);
      loadData();
    } catch (err) {
      console.error('Error saving service:', err);
      throw err;
    }
  };

  const handleAddCategory = () => {
    setSelectedCategoryData(null);
    setShowCategoryModal(true);
  };

  const handleEditCategory = (category) => {
    setSelectedCategoryData(category);
    setShowCategoryModal(true);
  };

  const handleDeleteCategory = async (categoryId) => {
    if (!window.confirm('Are you sure you want to delete this category? All services in this category will need to be reassigned.')) {
      return;
    }

    try {
      await serviceConfigService?.deleteCategory(categoryId);
      loadData();
    } catch (err) {
      console.error('Error deleting category:', err);
      setError(err?.message);
    }
  };

  const handleSaveCategory = async (categoryData) => {
    try {
      if (selectedCategoryData) {
        await serviceConfigService?.updateCategory(selectedCategoryData?.id, categoryData);
      } else {
        await serviceConfigService?.createCategory(categoryData, tenantId);
      }
      // Refresh category list immediately
      await loadData();
      setShowCategoryModal(false);
      setSelectedCategoryData(null);
    } catch (err) {
      console.error('Error saving category:', err);
      throw err;
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center flex-1">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto p-6">
      <ComponentIdBadge 
        id="pg-service-config-main" 
        name="Service Configuration Page"
        description="Service and category management interface"
        position="top-right"
      />
      <Sidebar isOpen={true} onClose={() => {}} />
      
      <div className="flex-1 flex flex-col overflow-hidden">
        <Header onToggleSidebar={() => {}} />
        
        <main className="flex-1 overflow-y-auto p-6">
          <div className="max-w-7xl mx-auto">
            <div className="flex justify-between items-center mb-6 relative">
              <div>
                <ComponentIdBadge
                  id="hdr-service-config-title"
                  name="Service Configuration Header"
                  description="Page title and description"
                  position="inline"
                  showLabel={true}
                />
                <h1 className="text-3xl font-bold text-gray-900">Service Configuration</h1>
                <p className="text-gray-600">Manage your service offerings and categories</p>
              </div>
              <div className="flex gap-2 relative">
                <SampleDataToggle
                  entityType="services"
                  onToggle={handleSampleDataToggle}
                  isActive={showSampleData}
                />
                <ComponentIdBadge
                  id="btn-service-add"
                  name="Add Service Button"
                  description="Opens modal to create new service"
                />
                {activeTab === 'services' && (
                  <Button onClick={handleAddService} className="flex items-center gap-2">
                    <Plus className="w-4 h-4" />
                    Add Service
                  </Button>
                )}
                {activeTab === 'categories' && (
                  <Button onClick={handleAddCategory} className="flex items-center gap-2">
                    <Plus className="w-4 h-4" />
                    Add Category
                  </Button>
                )}
              </div>
            </div>

            {error && (
              <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg text-red-600">
                {error}
              </div>
            )}

            {/* Tab Navigation */}
            <div className="mb-6 border-b border-gray-200">
              <nav className="flex gap-8">
                <button
                  onClick={() => setActiveTab('services')}
                  className={`pb-4 px-1 border-b-2 font-medium text-sm transition-colors ${
                    activeTab === 'services' ?'border-blue-600 text-blue-600' :'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                >
                  Services
                </button>
                <button
                  onClick={() => setActiveTab('categories')}
                  className={`pb-4 px-1 border-b-2 font-medium text-sm transition-colors ${
                    activeTab === 'categories' ?'border-blue-600 text-blue-600' :'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                >
                  Categories
                </button>
              </nav>
            </div>

            {/* Services Tab Content */}
            {activeTab === 'services' && (
              <>
                <div className="relative bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
                  <ComponentIdBadge 
                    id="cmp-service-filters" 
                    name="Service Filters Component"
                    description="Search and category filter controls"
                  />
                  <div className="flex gap-4">
                    <div className="flex-1">
                      <div className="relative">
                        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                        <input
                          type="text"
                          placeholder="Search services..."
                          value={searchQuery}
                          onChange={(e) => setSearchQuery(e?.target?.value)}
                          className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        />
                      </div>
                    </div>
                    <select
                      value={selectedCategory}
                      onChange={(e) => setSelectedCategory(e?.target?.value)}
                      className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    >
                      <option value="all">All Categories</option>
                      {categories?.map(category => (
                        <option key={category?.id} value={category?.id}>{category?.name}</option>
                      ))}
                    </select>
                  </div>
                </div>

                {displayServices?.length === 0 ? (
                  <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-12 text-center">
                    <Settings className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                    <h3 className="text-lg font-medium text-gray-900 mb-2">No services found</h3>
                    <p className="text-gray-500 mb-4">
                      {searchQuery || selectedCategory !== 'all' ?'Try adjusting your filters to see more services' :'Get started by creating your first service'}
                    </p>
                    <Button onClick={handleAddService}>
                      <Plus className="w-4 h-4 mr-2" />
                      Add Service
                    </Button>
                  </div>
                ) : (
                  <div className="relative grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    <ComponentIdBadge 
                      id="cmp-service-cards" 
                      name="Service Cards Grid"
                      description="Grid display of service cards with details and actions"
                    />
                    {displayServices?.map(service => (
                      <div key={service?.id} className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                        <div className="flex justify-between items-start mb-4">
                          <div className="flex-1">
                            <h3 className="text-lg font-semibold text-gray-900 mb-1">{service?.name}</h3>
                            <p className="text-sm text-gray-600">{service?.description}</p>
                          </div>
                          <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                            service?.is_active ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
                          }`}>
                            {service?.is_active ? 'Active' : 'Inactive'}
                          </span>
                        </div>
                        
                        <div className="space-y-2 mb-4">
                          <div className="flex justify-between text-sm">
                            <span className="text-gray-600">Duration:</span>
                            <span className="font-medium">{service?.duration} minutes</span>
                          </div>
                          <div className="flex justify-between text-sm">
                            <span className="text-gray-600">Price:</span>
                            <span className="font-medium">${service?.price}</span>
                          </div>
                          <div className="flex justify-between text-sm">
                            <span className="text-gray-600">Buffer Time:</span>
                            <span className="font-medium">{service?.buffer_time} minutes</span>
                          </div>
                          <div className="flex justify-between text-sm">
                            <span className="text-gray-600">Online Booking:</span>
                            <span className={`font-medium ${service?.online_booking_enabled ? 'text-green-600' : 'text-gray-600'}`}>
                              {service?.online_booking_enabled ? 'Enabled' : 'Disabled'}
                            </span>
                          </div>
                        </div>

                        <div className="flex gap-2">
                          <Button
                            onClick={() => handleEditService(service)}
                            variant="outline"
                            size="sm"
                            className="flex-1"
                          >
                            Edit
                          </Button>
                          <Button
                            onClick={() => handleToggleService(service?.id, service?.is_active)}
                            variant="outline"
                            size="sm"
                            className="flex-1"
                          >
                            {service?.is_active ? 'Deactivate' : 'Activate'}
                          </Button>
                          <Button
                            onClick={() => handleDeleteService(service?.id)}
                            variant="outline"
                            size="sm"
                            className="text-red-600 hover:bg-red-50"
                          >
                            Delete
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </>
            )}

            {/* Categories Tab Content */}
            {activeTab === 'categories' && (
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                <ComponentIdBadge 
                  id="cmp-category-management" 
                  name="Category Management Component"
                  description="Interface for managing service categories"
                />
                
                {categories?.length === 0 ? (
                  <div className="text-center py-12">
                    <Settings className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                    <h3 className="text-lg font-medium text-gray-900 mb-2">No categories yet</h3>
                    <p className="text-gray-500 mb-4">
                      Create your first service category to organize your services
                    </p>
                    <Button onClick={handleAddCategory}>
                      <Plus className="w-4 h-4 mr-2" />
                      Add Category
                    </Button>
                  </div>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {categories?.map(category => (
                      <div
                        key={category?.id}
                        className="border border-gray-200 rounded-lg p-6 hover:shadow-md transition-shadow"
                      >
                        <div className="flex items-start justify-between mb-4">
                          <div className="flex items-center gap-3">
                            <div
                              className="w-12 h-12 rounded-lg flex-shrink-0"
                              style={{ backgroundColor: category?.color }}
                            />
                            <div>
                              <h3 className="font-semibold text-gray-900">{category?.name}</h3>
                              {category?.description && (
                                <p className="text-sm text-gray-500 mt-1">{category?.description}</p>
                              )}
                            </div>
                          </div>
                        </div>
                        
                        <div className="flex gap-2 mt-4">
                          <Button
                            onClick={() => handleEditCategory(category)}
                            variant="outline"
                            size="sm"
                            className="flex-1"
                          >
                            Edit
                          </Button>
                          <Button
                            onClick={() => handleDeleteCategory(category?.id)}
                            variant="outline"
                            size="sm"
                            className="text-red-600 hover:bg-red-50"
                          >
                            Delete
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        </main>
      </div>

      {showServiceModal && (
        <div className="relative">
          <ComponentIdBadge 
            id="mdl-service-edit" 
            name="Service Details Modal"
            description="Modal for creating/editing service information"
            position="top-right"
          />
          <ServiceModal
            service={selectedService}
            categories={categories}
            isOpen={showServiceModal}
            onClose={() => setShowServiceModal(false)}
            onSave={handleSaveService}
          />
        </div>
      )}

      {showCategoryModal && (
        <div className="relative">
          <ComponentIdBadge 
            id="mdl-category-edit" 
            name="Category Details Modal"
            description="Modal for creating/editing service categories"
            position="top-right"
          />
          <CategoryModal
            category={selectedCategoryData}
            isOpen={showCategoryModal}
            onClose={() => setShowCategoryModal(false)}
            onSave={handleSaveCategory}
          />
        </div>
      )}
    </div>
  );
}